// src/pages/CustomerForm.jsx

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from '../../axios';
import {
  Container, Paper, Typography, Grid, TextField, Button, Box, Switch,
  CircularProgress, Dialog, DialogContent, InputAdornment, ToggleButton,
  ToggleButtonGroup, Alert, Stack, FormControlLabel, Divider
} from '@mui/material';
import {
  Person as PersonIcon,
  Business as BusinessIcon,
  CheckCircle as CheckIcon,
  LocationOn as LocationIcon,
} from '@mui/icons-material';

const PRIMARY = '#1e40af';
const BORDER = '#e5e7eb';

// حقول مضغوطة
const fieldSx = {
  '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: 14, bgcolor: '#fff' },
  '& .MuiInputLabel-root': { fontSize: 14 },
  '& .MuiFormHelperText-root': { mx: 0.5, mt: 0.25, fontSize: 11 },
};

const emptyForm = {
  customerType: 1,
  code: '',
  name: '',
  taxNumber: '',
  isTaxable: false,
  mobile: '',
  phone: '',
  email: '',
  buildingNumber: '',
  streetName: '',
  district: '',
  city: '',
  postalCode: '',
  additionalNumber: '',
  unitNumber: '',
  shortAddress: '',
  country: 'السعودية',
  address: '',
  creditLimit: 0,
  balance: 0,
  notes: '',
  isActive: true,
};

const DIGIT_FIELDS = {
  buildingNumber: 4, additionalNumber: 4, postalCode: 5, unitNumber: 4,
  taxNumber: 15, mobile: 10, phone: 10,
};

function validate(form) {
  const e = {};
  const isBusiness = Number(form.customerType) === 2;

  if (!form.name.trim()) e.name = 'مطلوب';
  if (form.mobile && !/^05\d{8}$/.test(form.mobile)) e.mobile = 'صيغة 05XXXXXXXX';
  if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'بريد غير صحيح';

  if (isBusiness || form.isTaxable) {
    if (!form.taxNumber) e.taxNumber = 'مطلوب';
    else if (!/^3\d{13}3$/.test(form.taxNumber)) e.taxNumber = '15 رقمًا تبدأ وتنتهي بـ 3';
  }

  if (isBusiness) {
    ['buildingNumber', 'streetName', 'district', 'city', 'postalCode'].forEach(k => {
      if (!String(form[k]).trim()) e[k] = 'مطلوب';
    });
  }

  if (form.buildingNumber && !/^\d{4}$/.test(form.buildingNumber)) e.buildingNumber = '4 أرقام';
  if (form.additionalNumber && !/^\d{4}$/.test(form.additionalNumber)) e.additionalNumber = '4 أرقام';
  if (form.postalCode && !/^\d{5}$/.test(form.postalCode)) e.postalCode = '5 أرقام';
  if (form.shortAddress && !/^[A-Z]{4}\d{4}$/.test(form.shortAddress)) e.shortAddress = 'مثال: RRRD2929';
  if (Number(form.creditLimit) < 0) e.creditLimit = 'لا يقبل السالب';

  return e;
}

// عنوان قسم صغير
const SectionTitle = ({ children, extra }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2.5, mb: 1.5 }}>
    <Box sx={{ width: 3, height: 16, bgcolor: PRIMARY, borderRadius: 1 }} />
    <Typography fontSize={14} fontWeight={700} color={PRIMARY}>{children}</Typography>
    <Divider sx={{ flex: 1 }} />
    {extra}
  </Box>
);

export default function CustomerForm({ onSuccess, onCancel, isModal = false }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const editingId = !isModal && id ? parseInt(id, 10) : null;

  const companyId = parseInt(localStorage.getItem('companyId'), 10) || 0;
  const branchId = parseInt(localStorage.getItem('branchId'), 10) || 0;

  const [form, setForm] = useState(emptyForm);
  const [touched, setTouched] = useState({});
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [serverError, setServerError] = useState('');
  const [successModal, setSuccessModal] = useState(false);

  const isBusiness = Number(form.customerType) === 2;
  const errors = useMemo(() => validate(form), [form]);

  useEffect(() => {
    if (!editingId) return;
    setFetching(true);
    axios.get(`/Customer/${editingId}`)
      .then(({ data: d }) => {
        const loaded = { ...emptyForm };
        Object.keys(emptyForm).forEach(k => {
          if (d[k] !== undefined && d[k] !== null) loaded[k] = d[k];
        });
        setForm(loaded);
      })
      .catch(() => setServerError('تعذّر تحميل بيانات العميل'))
      .finally(() => setFetching(false));
  }, [editingId]);

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    let v = type === 'checkbox' ? checked : value;
    if (DIGIT_FIELDS[name]) v = String(v).replace(/\D/g, '').slice(0, DIGIT_FIELDS[name]);
    if (name === 'shortAddress') v = String(v).toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8);
    setForm(prev => ({ ...prev, [name]: v }));
  };

  const handleBlur = e => setTouched(prev => ({ ...prev, [e.target.name]: true }));

  // خصائص الحقل — رسالة الخطأ تظهر فقط عند وجود خطأ (لا مساحة فارغة)
  const f = (name, props = {}) => {
    const showErr = Boolean(touched[name] && errors[name]);
    return {
      name,
      value: form[name] ?? '',
      onChange: handleChange,
      onBlur: handleBlur,
      error: showErr,
      helperText: showErr ? errors[name] : undefined,
      size: 'small',
      fullWidth: true,
      sx: fieldSx,
      ...props,
    };
  };
  const ltr = { inputProps: { dir: 'ltr', inputMode: 'numeric' } };

  const addressPreview = useMemo(() => {
    const parts = [
      [form.buildingNumber, form.streetName].filter(Boolean).join(' '),
      form.district && `حي ${form.district}`,
      form.city,
      form.postalCode,
      form.additionalNumber && `إضافي ${form.additionalNumber}`,
      form.country,
    ].filter(Boolean);
    return parts.join('، ');
  }, [form]);

  const handleSubmit = async e => {
    e.preventDefault();
    setServerError('');

    if (Object.keys(errors).length) {
      setTouched(Object.keys(form).reduce((a, k) => ({ ...a, [k]: true }), {}));
      setServerError('يرجى تصحيح الحقول المظللة');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...form,
        customerType: Number(form.customerType),
        creditLimit: Number(form.creditLimit) || 0,
        address: form.address || addressPreview,
        companyId,
        branchId,
      };
      delete payload.balance;

      const response = editingId
        ? await axios.put(`/Customer/${editingId}`, payload)
        : await axios.post('/Customer', payload);

      setSuccessModal(true);
      setTimeout(() => {
        setSuccessModal(false);
        if (onSuccess) onSuccess({ ...form, id: response.data?.id || response.data?.Id, ...response.data });
        else navigate('/customers');
      }, 1300);
    } catch (err) {
      const data = err.response?.data;
      const firstModelError = data?.errors && Object.values(data.errors)?.[0]?.[0];
      setServerError(firstModelError || data?.title || data?.message || 'حدث خطأ أثناء الحفظ');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => (onCancel ? onCancel() : navigate('/customers'));

  if (fetching) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress size={28} /></Box>;
  }

  return (
    <Container maxWidth="md" disableGutters={isModal} sx={{ my: isModal ? 0 : 3 }} dir="rtl">
      <Paper elevation={0} sx={{ borderRadius: '12px', border: `1px solid ${BORDER}`, overflow: 'hidden' }}>
        {/* الترويسة */}
        <Box sx={{
          px: 2.5, py: 1.5, borderBottom: `1px solid ${BORDER}`, bgcolor: '#f9fafb',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2,
        }}>
          <Typography fontSize={17} fontWeight={800}>
            {editingId ? 'تعديل بيانات العميل' : 'عميل جديد'}
          </Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={Number(form.customerType)}
            onChange={(_, v) => v && setForm(p => ({ ...p, customerType: v, isTaxable: v === 2 ? true : p.isTaxable }))}
            sx={{
              '& .MuiToggleButton-root': { px: 1.5, py: 0.4, gap: 0.5, fontSize: 13, fontWeight: 600 },
              '& .Mui-selected': { bgcolor: `${PRIMARY} !important`, color: '#fff !important' },
            }}
          >
            <ToggleButton value={1}><PersonIcon sx={{ fontSize: 16 }} /> فردي</ToggleButton>
            <ToggleButton value={2}><BusinessIcon sx={{ fontSize: 16 }} /> تجاري</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Box component="form" onSubmit={handleSubmit} noValidate sx={{ px: 2.5, pb: 2.5 }}>
          {serverError && (
            <Alert severity="error" sx={{ mt: 2, py: 0, borderRadius: '8px' }} onClose={() => setServerError('')}>
              {serverError}
            </Alert>
          )}

          {/* البيانات الأساسية */}
          <SectionTitle>البيانات الأساسية</SectionTitle>
          <Grid container spacing={1.5}>
            <Grid item xs={4} md={2}>
              <TextField label="الرمز" placeholder="تلقائي" {...f('code')} />
            </Grid>
            <Grid item xs={8} md={10}>
              <TextField label={isBusiness ? 'اسم المنشأة' : 'الاسم'} required {...f('name')} />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField label="الجوال" placeholder="05XXXXXXXX" {...f('mobile', { inputProps: { dir: 'ltr', inputMode: 'tel' } })} />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField label="الهاتف" {...f('phone', { inputProps: { dir: 'ltr', inputMode: 'tel' } })} />
            </Grid>
            <Grid item xs={12} md={5}>
              <TextField label="البريد الإلكتروني" type="email" {...f('email', { inputProps: { dir: 'ltr' } })} />
            </Grid>
          </Grid>

          {/* الضريبة */}
          <SectionTitle
            extra={
              <FormControlLabel
                sx={{ m: 0, '& .MuiFormControlLabel-label': { fontSize: 13 } }}
                control={<Switch size="small" name="isTaxable" checked={form.isTaxable || isBusiness} onChange={handleChange} disabled={isBusiness} />}
                label="خاضع للضريبة"
              />
            }
          >
            الضريبة
          </SectionTitle>
          <Grid container spacing={1.5}>
            <Grid item xs={12} md={5}>
              <TextField
                label="الرقم الضريبي"
                required={isBusiness || form.isTaxable}
                disabled={!isBusiness && !form.isTaxable}
                placeholder="3XXXXXXXXXXXXX3"
                {...f('taxNumber', ltr)}
                InputProps={{
                  endAdornment: <InputAdornment position="end"><Typography fontSize={11} color="text.secondary">{form.taxNumber.length}/15</Typography></InputAdornment>,
                }}
              />
            </Grid>
            <Grid item xs={6} md={4}>
              <TextField
                label="حد الائتمان"
                type="number"
                {...f('creditLimit', { inputProps: { min: 0, step: '0.01', dir: 'ltr' } })}
                InputProps={{ endAdornment: <InputAdornment position="end"><Typography fontSize={12}>ر.س</Typography></InputAdornment> }}
              />
            </Grid>
            {editingId && (
              <Grid item xs={6} md={3}>
                <TextField
                  label="الرصيد"
                  size="small"
                  fullWidth
                  disabled
                  sx={fieldSx}
                  value={Number(form.balance).toFixed(2)}
                  inputProps={{ dir: 'ltr' }}
                />
              </Grid>
            )}
          </Grid>

          {/* العنوان الوطني */}
          <SectionTitle>العنوان الوطني</SectionTitle>
          <Grid container spacing={1.5}>
            <Grid item xs={4} md={2}>
              <TextField label="رقم المبنى" required={isBusiness} placeholder="1234" {...f('buildingNumber', ltr)} />
            </Grid>
            <Grid item xs={8} md={4}>
              <TextField label="الشارع" required={isBusiness} {...f('streetName')} />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField label="الحي" required={isBusiness} {...f('district')} />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField label="المدينة" required={isBusiness} {...f('city')} />
            </Grid>

            <Grid item xs={4} md={2}>
              <TextField label="الرمز البريدي" required={isBusiness} placeholder="12345" {...f('postalCode', ltr)} />
            </Grid>
            <Grid item xs={4} md={2}>
              <TextField label="الرقم الإضافي" placeholder="5678" {...f('additionalNumber', ltr)} />
            </Grid>
            <Grid item xs={4} md={2}>
              <TextField label="رقم الوحدة" {...f('unitNumber', ltr)} />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                label="العنوان المختصر"
                placeholder="RRRD2929"
                {...f('shortAddress', { inputProps: { dir: 'ltr', style: { letterSpacing: 1 } } })}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField label="الدولة" {...f('country')} />
            </Grid>
          </Grid>

          {addressPreview && addressPreview !== form.country && (
            <Box sx={{
              mt: 1.5, px: 1.5, py: 1, borderRadius: '8px', bgcolor: '#f8fafc',
              border: `1px dashed ${BORDER}`, display: 'flex', alignItems: 'center', gap: 1,
            }}>
              <LocationIcon sx={{ fontSize: 16, color: PRIMARY }} />
              <Typography fontSize={13} color="text.secondary">{addressPreview}</Typography>
            </Box>
          )}

          {/* ملاحظات */}
          <SectionTitle
            extra={
              <FormControlLabel
                sx={{ m: 0, '& .MuiFormControlLabel-label': { fontSize: 13 } }}
                control={<Switch size="small" color="success" name="isActive" checked={form.isActive} onChange={handleChange} />}
                label={form.isActive ? 'نشط' : 'موقوف'}
              />
            }
          >
            أخرى
          </SectionTitle>
          <TextField label="ملاحظات" multiline minRows={2} {...f('notes')} />
        </Box>

        {/* الأزرار */}
        <Stack
          direction="row"
          spacing={1}
          justifyContent="flex-end"
          sx={{ px: 2.5, py: 1.5, borderTop: `1px solid ${BORDER}`, bgcolor: '#f9fafb' }}
        >
          <Button
            variant="text"
            onClick={handleCancel}
            disabled={loading}
            sx={{ borderRadius: '8px', px: 2.5, color: 'text.secondary', fontWeight: 600 }}
          >
            إلغاء
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
            disableElevation
            sx={{ borderRadius: '8px', px: 4, fontWeight: 700, bgcolor: PRIMARY, minWidth: 120 }}
          >
            {loading ? <CircularProgress size={20} color="inherit" /> : 'حفظ'}
          </Button>
        </Stack>
      </Paper>

      <Dialog open={successModal} PaperProps={{ sx: { borderRadius: '14px', width: 280, textAlign: 'center' } }}>
        <DialogContent>
          <CheckIcon sx={{ fontSize: 56, color: '#22c55e' }} />
          <Typography fontWeight={700} mt={1}>تم الحفظ بنجاح</Typography>
        </DialogContent>
      </Dialog>
    </Container>
  );
}